import React, { useMemo } from 'react';
import { useGetLabelsQuery } from '@features/appointment-labels';
import { useOrganizationId } from '@features/user';
import { Autocomplete, Box, TextField } from '@mui/material';

interface LabelOption {
  id: string;
  name: string;
}

export interface LabelFilterControlProps {
  /** Currently selected label ids (the filter's `values`). */
  value: string[];
  /** Emits the selected label ids (op is always `any_of` upstream). */
  onChange: (labelIds: string[]) => void;
}

/**
 * Value editor for the Schedule report's `labels` filter. The report column
 * displays label names but filters on the hidden `label_ids` list, so this
 * control pulls the org's label catalog, shows labels by NAME, and emits the
 * selected label IDs. Plain multi-select across all labels (no group-swap, no
 * color swatches) — a report filter, not the appointment label picker.
 */
export const LabelFilterControl: React.FC<LabelFilterControlProps> = ({ value, onChange }) => {
  const organizationId = useOrganizationId();
  const { data } = useGetLabelsQuery({ orgId: organizationId ?? '' }, { skip: !organizationId });

  const options = useMemo<LabelOption[]>(
    () => (data?.items ?? []).map(label => ({ id: label.label_id, name: label.name })),
    [data],
  );

  const selected = useMemo(
    () => options.filter(option => value.includes(option.id)),
    [options, value],
  );

  return (
    <Autocomplete
      size="small"
      multiple
      disableCloseOnSelect
      options={options}
      value={selected}
      getOptionLabel={option => option.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, next) => onChange(next.map(option => option.id))}
      slotProps={{
        popper: {
          placement: 'bottom-start',
          sx: { minWidth: 220, maxWidth: 400, width: 'auto !important' },
        },
        chip: { size: 'small', sx: { fontSize: '0.6875rem', height: 20 } },
      }}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.id}
          sx={{ fontSize: '0.75rem !important', whiteSpace: 'nowrap' }}
        >
          {option.name}
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          size="small"
          placeholder="labels"
          sx={{ '& .MuiInputBase-root': { fontSize: '0.75rem' } }}
        />
      )}
      sx={{ flex: 1, minWidth: 160 }}
    />
  );
};
